/**
 * Group divisions by sport and/or gender when their names say so.
 *
 * "U10 Boys", "U12 Boys", "U10 Girls" → Boys: U10, U12 · Girls: U10. The word
 * the group is named for is dropped from the shown name; the division's
 * real name is still what gets registered. An axis only groups when the
 * names carry at least two of its values (all-Boys lists stay as they are).
 * When both axes vary, groups combine them ("Softball · Girls"). Divisions
 * that name neither go last, under "Other". Order follows the organizer's
 * display order: groups by first appearance, divisions within a group as
 * listed.
 */

interface Term {
  label: string;
  re: RegExp;
}

// Longer phrases before the words they contain ("Flag Football" before "Football").
const SPORTS: Term[] = [
  ['Baseball', 'baseball'],
  ['Softball', 'softball'],
  ['Basketball', 'basketball'],
  ['Volleyball', 'volleyball'],
  ['Soccer', 'soccer'],
  ['Lacrosse', 'lacrosse'],
  ['Hockey', 'hockey'],
  ['Flag Football', 'flag\\s+football'],
  ['Football', 'football'],
].map(([label, src]) => ({ label, re: new RegExp(`\\b${src}\\b`, 'i') }));

const GENDERS: Term[] = [
  ['Boys', "boy(?:s|'s|s')?"],
  ['Girls', "girl(?:s|'s|s')?"],
  ['Coed', 'co-?ed'],
  ["Women's", "women(?:'s|s)?"],
  ["Men's", "men(?:'s|s)?"],
].map(([label, src]) => ({ label, re: new RegExp(`\\b${src}(?=$|[^\\w'])`, 'i') }));

function termFor(name: string, terms: Term[]): Term | null {
  return terms.find((t) => t.re.test(name)) ?? null;
}

/** Drop the matched words, then tidy the separators left around them. */
function stripTerms(name: string, terms: Term[]): string {
  let out = name;
  for (const t of terms) out = out.replace(new RegExp(t.re.source, 'gi'), ' ');
  out = out
    .replace(/\(\s*\)/g, ' ')
    .replace(/\s*([-–—·|/,])\s*(?=[-–—·|/,]|$)/g, ' ')
    .replace(/^\s*[-–—·|/,]\s*/, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  return out || name;
}

export interface DivisionGroup<T> {
  /** Null when the list isn't grouped (show no header). */
  label: string | null;
  items: Array<{ division: T; display: string }>;
}

export function groupDivisions<T extends { name: string }>(divisions: T[]): DivisionGroup<T>[] {
  const sports = divisions.map((d) => termFor(d.name, SPORTS));
  const genders = divisions.map((d) => termFor(d.name, GENDERS));
  const distinct = (xs: Array<Term | null>) => new Set(xs.filter(Boolean).map((t) => t!.label)).size;
  const bySport = distinct(sports) >= 2;
  const byGender = distinct(genders) >= 2;
  if (!bySport && !byGender) {
    return [{ label: null, items: divisions.map((d) => ({ division: d, display: d.name })) }];
  }

  const groups = new Map<string, DivisionGroup<T>>();
  const other: DivisionGroup<T> = { label: 'Other', items: [] };
  divisions.forEach((d, i) => {
    const parts = [bySport ? sports[i] : null, byGender ? genders[i] : null].filter(Boolean) as Term[];
    if (parts.length === 0) {
      other.items.push({ division: d, display: d.name });
      return;
    }
    const label = parts.map((t) => t.label).join(' · ');
    if (!groups.has(label)) groups.set(label, { label, items: [] });
    groups.get(label)!.items.push({ division: d, display: stripTerms(d.name, parts) });
  });
  const out = [...groups.values()];
  if (other.items.length) out.push(other);
  return out;
}
